import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { api, formatApiError } from "@/lib/kfmApi";
import { Button } from "@/components/ui/button";
import { Check, Sparkles, Loader2, ArrowLeft, Heart, ShieldCheck } from "lucide-react";
import { toast } from "sonner";

const TEAL = "#00E5FF";

function fmtDate(unix) {
  if (!unix) return "";
  try {
    return new Date(unix * 1000).toLocaleDateString(undefined, { month: "long", day: "numeric", year: "numeric" });
  } catch {
    return "";
  }
}

export default function Billing() {
  const navigate = useNavigate();
  const [sub, setSub] = useState(null);
  const [busy, setBusy] = useState("");

  const load = useCallback(async () => {
    try {
      const { data } = await api.get("/me/subscription");
      setSub(data);
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const goCheckout = async (lookup_key, key) => {
    setBusy(key);
    try {
      const { data } = await api.post("/payments/checkout", {
        lookup_key,
        origin_url: window.location.origin,
      });
      window.location.href = data.checkout_url;
    } catch (e) {
      setBusy("");
      toast.error(formatApiError(e.response?.data?.detail));
    }
  };

  const switchPlan = async (lookup_key, key) => {
    setBusy(key);
    try {
      await api.post("/billing/change", { lookup_key, origin_url: window.location.origin });
      toast.success("Your plan has been updated.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy("");
  };

  const cancelPlan = async () => {
    setBusy("cancel");
    try {
      await api.post("/billing/cancel");
      toast.success("Your plan will stay active until the end of this billing period.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy("");
  };

  const resumePlan = async () => {
    setBusy("resume");
    try {
      await api.post("/billing/resume");
      toast.success("Welcome back — your plan will continue.");
      await load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
    }
    setBusy("");
  };

  if (!sub) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center">
        <Loader2 className="w-6 h-6 animate-spin" style={{ color: TEAL }} strokeWidth={1.5} />
      </div>
    );
  }

  const current = sub.tier;
  const canceling = sub.cancel_at_period_end;
  const RANK = { free: 0, basic: 1, pro: 2, team: 3 };
  const curRank = RANK[current] ?? 0;

  return (
    <div className="px-6 py-10 space-y-10 md:max-w-3xl md:mx-auto md:py-14 animate-in fade-in slide-in-from-bottom-4 duration-700">
      <header className="space-y-2">
        <button
          data-testid="billing-back"
          onClick={() => navigate("/settings")}
          className="flex items-center gap-1.5 text-sm text-slate-400 hover:text-slate-300 transition-colors"
        >
          <ArrowLeft className="w-4 h-4" strokeWidth={1.5} /> Settings
        </button>
        <h1 className="text-3xl text-white">Your plan</h1>
        <p className="text-sm text-slate-400">
          OpenWord AI is a study tool. Everything here simply keeps the lights on and the virtual study companion running —
          the Word remains the belief system.
        </p>
      </header>

      {/* Current status */}
      <section className="rounded-2xl bg-[#10222A] border border-[#00E5FF]/15 p-5 space-y-3" data-testid="billing-current">
        <div className="flex items-baseline justify-between">
          <span className="text-2xl text-white" data-testid="billing-current-tier">{sub.plan?.name}</span>
          <span className="text-sm text-slate-400">{sub.plan?.price ? `$${sub.plan.price}/mo` : "Free"}</span>
        </div>
        <div className="flex gap-8 text-sm text-slate-400">
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Credits</div>
            <div className="text-lg text-slate-200" data-testid="billing-credits">{sub.uncapped ? "Unlimited" : sub.credits_total}</div>
            {!sub.uncapped && sub.credits_topup > 0 && (
              <div className="text-xs text-slate-500">{sub.credits} monthly + {sub.credits_topup} top-up</div>
            )}
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Go Deeper</div>
            <div className="text-lg text-slate-200">
              {sub.uncapped ? "Unlimited" : "1 credit each"}
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-widest text-slate-500">Virtual study companion</div>
            <div className="text-lg text-slate-200">{sub.model_label}</div>
          </div>
        </div>
        {canceling && (
          <div className="pt-2 border-t border-[#00E5FF]/15 space-y-2" data-testid="billing-canceling-notice">
            <p className="text-sm text-slate-400">
              Your {sub.plan?.name} plan stays active until <strong>{fmtDate(sub.current_period_end)}</strong>,
              then gently returns to Free. You&apos;re always welcome back &mdash; no hard feelings.
            </p>
            <Button
              data-testid="billing-resume"
              onClick={resumePlan}
              disabled={busy === "resume"}
              className="rounded-full text-[#07090E]"
              style={{ backgroundColor: TEAL }}
            >
              {busy === "resume" ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Heart className="w-4 h-4 mr-2" strokeWidth={1.5} /> Keep my plan</>}
            </Button>
          </div>
        )}
      </section>

      {/* Plans */}
      <section className="space-y-4">
        <div className="text-xs font-medium uppercase tracking-widest text-slate-400">Plans</div>
        <div className="grid gap-4 md:grid-cols-2">
          {sub.plans.map((p) => {
            const isCurrent = p.id === current;
            const pRank = RANK[p.id] ?? 0;
            const isUpgrade = pRank > curRank;
            const isDowngrade = pRank < curRank;
            let action = null;
            if (isCurrent) {
              action = (
                <div className="flex items-center gap-1.5 text-sm font-medium" style={{ color: TEAL }} data-testid={`billing-current-badge-${p.id}`}>
                  <Check className="w-4 h-4" strokeWidth={2} /> Current plan
                </div>
              );
            } else if (p.id === "free") {
              action = canceling ? (
                <span className="text-xs text-slate-500">Returns to Free at period end</span>
              ) : (
                <Button
                  data-testid="billing-return-free"
                  variant="outline"
                  onClick={cancelPlan}
                  disabled={busy === "cancel"}
                  className="rounded-full border-[#29344D] text-slate-400 hover:text-slate-300"
                >
                  {busy === "cancel" ? <Loader2 className="w-4 h-4 animate-spin" /> : "Downgrade to Free"}
                </Button>
              );
            } else if (current === "free") {
              action = (
                <Button
                  data-testid={`billing-subscribe-${p.id}`}
                  onClick={() => goCheckout(p.lookup_key, `sub-${p.id}`)}
                  disabled={!!busy}
                  className="rounded-full text-[#07090E]"
                  style={{ backgroundColor: TEAL }}
                >
                  {busy === `sub-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : "Subscribe"}
                </Button>
              );
            } else if (isUpgrade) {
              action = (
                <Button
                  data-testid={`billing-switch-${p.id}`}
                  onClick={() => switchPlan(p.lookup_key, `switch-${p.id}`)}
                  disabled={!!busy}
                  className="rounded-full text-[#07090E]"
                  style={{ backgroundColor: TEAL }}
                >
                  {busy === `switch-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Sparkles className="w-4 h-4 mr-1.5" strokeWidth={1.5} /> Upgrade to {p.name}</>}
                </Button>
              );
            } else {
              action = (
                <Button
                  data-testid={`billing-switch-${p.id}`}
                  variant="outline"
                  onClick={() => switchPlan(p.lookup_key, `switch-${p.id}`)}
                  disabled={!!busy}
                  className="rounded-full border-[#29344D] text-slate-400 hover:text-slate-300"
                >
                  {busy === `switch-${p.id}` ? <Loader2 className="w-4 h-4 animate-spin" /> : `Downgrade to ${p.name}`}
                </Button>
              );
            }
            return (
              <div
                key={p.id}
                data-testid={`billing-plan-${p.id}`}
                className={`rounded-2xl border p-5 space-y-3 bg-[#0E131F] transition-opacity ${isCurrent ? "border-[#00E5FF]/60 ring-2 ring-[#00E5FF]/25" : isDowngrade ? "border-[#1E273D] opacity-60" : "border-[#1E273D]"}`}
              >
                <div className="flex items-baseline justify-between">
                  <span className="text-xl text-white flex items-center gap-2">
                    {p.name}
                    {isCurrent && <span className="text-[10px] uppercase tracking-widest px-2 py-0.5 rounded-full bg-[#10222A] text-[#00E5FF] font-sans">Current</span>}
                  </span>
                  <span className="text-sm text-slate-400">{p.price ? `$${p.price}/mo` : "Free"}</span>
                </div>
                <p className="text-sm text-slate-400 leading-relaxed min-h-[40px]">{p.blurb}</p>
                <ul className="text-xs text-slate-400 space-y-1">
                  <li>{p.credits} study credits / month</li>
                  <li>Go Deeper · 1 credit each</li>
                </ul>
                <div className="pt-1">{action}</div>
              </div>
            );
          })}
        </div>
      </section>

      {/* Top-up */}
      {(sub.topup_packs?.length > 0) && (
        <section className="space-y-3">
          <div className="text-xs font-medium uppercase tracking-widest text-slate-400">Need more credits?</div>
          <p className="text-xs text-slate-500 -mt-1">One-time purchase — these credits never expire and stack on top of your monthly credits.</p>
          <div className="grid gap-3 sm:grid-cols-3" data-testid="billing-topup">
            {sub.topup_packs.map((pack) => (
              <div
                key={pack.lookup_key}
                data-testid={`billing-topup-${pack.lookup_key}`}
                className="rounded-2xl border border-[#1E273D] bg-[#0E131F] p-5 flex flex-col gap-3 text-center"
              >
                <Sparkles className="w-5 h-5 mx-auto" style={{ color: TEAL }} strokeWidth={1.5} />
                <div>
                  <div className="text-2xl text-white">{pack.credits}</div>
                  <div className="text-xs text-slate-500">credits</div>
                </div>
                <Button
                  data-testid={`billing-buy-topup-${pack.lookup_key}`}
                  onClick={() => goCheckout(pack.lookup_key, `topup-${pack.lookup_key}`)}
                  disabled={!!busy}
                  className="rounded-full text-[#07090E] w-full"
                  style={{ backgroundColor: TEAL }}
                >
                  {busy === `topup-${pack.lookup_key}` ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : `Buy for $${pack.price}`}
                </Button>
              </div>
            ))}
          </div>
        </section>
      )}

      <div className="pt-2 flex flex-col items-center gap-2 text-xs text-slate-500" data-testid="billing-trust-footer">
        <div className="flex items-center gap-1.5">
          <ShieldCheck className="w-3.5 h-3.5" style={{ color: TEAL }} strokeWidth={1.5} />
          <span>Secure payments by Stripe · Cancel anytime</span>
        </div>
        <div className="flex items-center gap-2">
          <a href="/privacy" target="_blank" rel="noopener noreferrer" data-testid="billing-privacy-link" className="hover:text-[#00E5FF] transition-colors">Privacy</a>
          <span className="text-slate-500">·</span>
          <a href="/terms" target="_blank" rel="noopener noreferrer" data-testid="billing-terms-link" className="hover:text-[#00E5FF] transition-colors">Terms</a>
        </div>
      </div>
    </div>
  );
}
